import {ContactRelationship, School, SchoolClassInvitation} from "models"
import React, {memo, useMemo} from "react"
import {shapeComponent, ShapeComponent} from "set-state-compare/build/shape-component"
import ApiMakerConfig from "@kaspernj/api-maker/build/config"
import AppHistory from "shared/history"
import ChalkyLayout from "components/chalky-layout"
import {digg} from "diggerize"
import ProUpgradeOverlay from "components/pro/upgrade-overlay"
import Routes from "shared/routes"
import setLayout from "shared/set-layout"
import useAnythingPro from "components/subscriptions/use-anything-pro"
import useCurrentUser from "@kaspernj/api-maker/build/use-current-user"
import useDestroyedEvent from "@kaspernj/api-maker/build/use-destroyed-event"
import useI18n from "i18n-on-steroids/build/src/use-i18n.js"
import useQueryParams from "on-location-changed/build/use-query-params"
import UsersTable from "./my-users/users-table"
import {View} from "react-native"

/** @typedef {object} RoutesUserMyUsersRouteProps */

/**
 * @typedef {object} RoutesUserMyUsersRouteState
 * @property {unknown} schoolClassesForSchoolsAndPeople
 * @property {unknown} schoolClassInvitationsByUserId
 * @property {unknown} schoolRolesPerSchool
 * @property {unknown} schools
 */

/**
 * @extends {ShapeComponent<RoutesUserMyUsersRouteProps, RoutesUserMyUsersRouteState>}
 */
class RoutesUserMyUsersRoute extends ShapeComponent {
  /** @type {RoutesUserMyUsersRouteState} */
  state = {
    schools: null,
    schoolRolesPerSchool: null,
    schoolClassesForSchoolsAndPeople: null,
    schoolClassInvitationsByUserId: null
  }

  setup() {
    setLayout("user")

    const {anythingPro} = useAnythingPro()
    const {t} = useI18n({namespace: "js.routes.user.my_users"})
    const currentUser = useCurrentUser()
    const queryParams = useQueryParams()

    this.anythingPro = anythingPro
    this.currentUser = currentUser
    this.t = t


    this.params = useMemo(() => {
      if (queryParams.my_users_params) {
        return JSON.parse(queryParams.my_users_params)
      } else {
        return {}
      }
    }, [queryParams.my_users_params])
    const schoolRoles = this.tt.schoolRolesFromState()

    useMemo(() => {
      if (currentUser && anythingPro) {
        this.loadSchools()
      } else {
        this.setState({schools: null})
      }
    }, [anythingPro, currentUser?.id()])
    useDestroyedEvent(schoolRoles, this.tt.onSchoolRoleDestroyed)
  }

  async loadSchools() {
    const {currentUser} = this.tt
    // Restrict to schools where the current user is directly attached as
    // admin or school_leader. A teacher-only role on a school must not
    // surface that school's user roster on /bruger/mine-brugere.
    const schools = await School
      .select({
        School: ["contactId", "displayName", "id"]
      })
      .ransack({
        with_school_role_contact_id: JSON.stringify({
          contact_id: currentUser.contactId(),
          roles: ["admin", "school_leader"]
        })
      })
      .groupBy("id")
      .toArray()

    const schoolContactIds = []
    const schoolRolesPerSchool = {}

    for (const school of schools) {
      schoolContactIds.push(school.contactId())
      schoolRolesPerSchool[school.contactId()] = {}
    }

    if (schools.length === 0) {
      // Ransack drops *_eq_any predicates for empty arrays, so avoid querying unrelated readable records.
      this.setState({
        schoolClassesForSchoolsAndPeople: {},
        schoolClassInvitationsByUserId: {},
        schoolRolesPerSchool,
        schools
      })

      return
    }

    this.loadSchoolInvitations(schools)

    const allSchoolRoles = await ContactRelationship
      .ransack({
        parent_id_eq_any: schoolContactIds,
        where_relationship_type: ["admin", "school_class", "school_leader", "teacher"]
      })
      .preload(["child.user", "contact_relationship_type"])
      .select({
        Contact: ["email", "id", "name"],
        ContactRelationship: ["childId", "id", "parentId"],
        ContactRelationshipType: ["identifier", "name"],
        User: ["id", "lastActivityAt", "name"]
      })
      .abilities({
        ContactRelationship: ["destroy", "edit"]
      })
      .toArray()

    const schoolRoles = []
    const schoolClassIds = []
    const schoolIdsForSchoolClases = {}

    for (const schoolRole of allSchoolRoles) {
      if (schoolRole.contactRelationshipType().identifier() == "school_class") {
        schoolIdsForSchoolClases[schoolRole.childId()] = schoolRole.parentId()
        schoolClassIds.push(schoolRole.childId())
      } else {
        schoolRoles.push(schoolRole)
      }
    }

    // Skip the lookup when no school classes are involved — `parent_id_eq_any: []`
    // does not constrain the query and would return any teacher rel visible via
    // ContactRelationshipResource#global_abilities (e.g. the user's own teacher
    // role on an unrelated school), which then has no matching entry in
    // schoolIdsForSchoolClases and trips the `digg` below.
    const schoolClassRoles = schoolClassIds.length === 0
      ? []
      : await ContactRelationship
        .ransack({
          parent_id_eq_any: schoolClassIds,
          where_relationship_type: ["teacher"]
        })
        .toArray()

    const schoolClassesForSchoolsAndPeople = {}

    for (const schoolClassRole of schoolClassRoles) {
      const schoolContactId = digg(schoolIdsForSchoolClases, schoolClassRole.parentId())

      if (!(schoolContactId in schoolClassesForSchoolsAndPeople)) {
        schoolClassesForSchoolsAndPeople[schoolContactId] = {}
      }

      if (!(schoolClassRole.childId() in schoolClassesForSchoolsAndPeople[schoolContactId])) {
        schoolClassesForSchoolsAndPeople[schoolContactId][schoolClassRole.childId()] = []
      }

      schoolClassesForSchoolsAndPeople[schoolContactId][schoolClassRole.childId()].push(schoolClassRole)
    }

    for (const schoolRole of schoolRoles) {
      const schoolRolesForSchool = schoolRolesPerSchool[schoolRole.parentId()]

      if (!(schoolRole.childId() in schoolRolesForSchool)) {
        schoolRolesForSchool[schoolRole.childId()] = {
          contact: schoolRole.child(),
          schoolRoles: []
        }
      }

      schoolRolesForSchool[schoolRole.childId()].schoolRoles.push(schoolRole)
    }

    this.setState({schools, schoolRolesPerSchool, schoolClassesForSchoolsAndPeople})
  }

  async loadSchoolInvitations(schools) {
    const schoolIds = schools.map((school) => school.id())
    const schoolClassInvitations = await SchoolClassInvitation
      .ransack({
        resource_type_eq: "School",
        resource_id_eq_any: schoolIds
      })
      .select({
        SchoolClassInvitation: ["createdUserId", "existingUserId", "state"]
      })
      .toArray()

    const schoolClassInvitationsByUserId = {}

    for (const schoolClassInvitation of schoolClassInvitations) {
      const userId = schoolClassInvitation.createdUserId() || schoolClassInvitation.existingUserId()

      schoolClassInvitationsByUserId[userId] = schoolClassInvitation
    }

    this.setState({schoolClassInvitationsByUserId})
  }

  render() {
    const {anythingPro, t} = this.tt
    const {schools, schoolClassesForSchoolsAndPeople, schoolClassInvitationsByUserId, schoolRolesPerSchool} = this.s
    const loaded = anythingPro === true && schools !== null && schoolClassInvitationsByUserId !== null
    const schoolCount = schools ? schools.length : 0

    return (
      <ChalkyLayout active="my-users" headerTitle={t(".my_users")} userMenu={false} weekBar={false}>
        <View
          dataSet={this.cache("routeViewDataSet", {loaded, schoolCount}, [loaded, schoolCount])}
          style={this.routeViewStyle ||= {minHeight: 1}}
          testID="routes/user/my-users-route"
        >
          {anythingPro === false &&
            <ProUpgradeOverlay onRequestClose={this.tt.onUpgradeOverlayRequestClose} />
          }
          {loaded && schools.map((school) =>
            <View
              dataSet={this.cache("usersTableViewDataSet", {testid: "my-users-users-table", schoolId: school.id()}, [school.id()])}
              key={school.id()}
              style={this.usersTableViewStyle ||= {marginBottom: 30}}
            >
              <UsersTable
                params={this.tt.params[school.id()]}
                school={school}
                schoolClassInvitationsByUserId={schoolClassInvitationsByUserId}
                schoolClassesForSchoolsAndPeople={schoolClassesForSchoolsAndPeople?.[school.contactId()] || {}}
                schoolRoles={schoolRolesPerSchool?.[school.contactId()] || {}}
              />
            </View>
          )}
        </View>
      </ChalkyLayout>
    )
  }

  onUpgradeOverlayRequestClose = ({reason} = {}) => {
    if (reason !== "stay-basic") {
      return
    }

    if (this.tt.anythingPro === false) {
      this.goBackOrDashboard()
    }
  }

  goBackOrDashboard() {
    const history = ApiMakerConfig.getHistory()

    if (history?.back) {
      history.back()

      return
    }

    if (history?.goBack) {
      history.goBack()

      return
    }

    if (history?.go && typeof history.length === "number" && history.length > 1) {
      history.go(-1)

      return
    }

    if (typeof window !== "undefined" && window.history?.length > 1) {
      window.history.back()

      return
    }

    AppHistory.push(Routes.userRootPath())
  }

  schoolRolesFromState() {
    if (!this.s.schoolRolesPerSchool) {
      return []
    }

    const schoolRoles = []

    for (const schoolRolesForSchool of Object.values(this.s.schoolRolesPerSchool)) {
      for (const schoolRoleData of Object.values(schoolRolesForSchool)) {
        for (const schoolRole of schoolRoleData.schoolRoles) {
          schoolRoles.push(schoolRole)
        }
      }
    }

    return schoolRoles
  }

  onSchoolRoleDestroyed = ({model: schoolRole}) => {
    if (!this.s.schoolRolesPerSchool) {
      return
    }

    const schoolRolesForSchool = this.s.schoolRolesPerSchool[schoolRole.parentId()]

    if (!schoolRolesForSchool) {
      return
    }

    const schoolRoleData = schoolRolesForSchool[schoolRole.childId()]

    if (!schoolRoleData) {
      return
    }

    const schoolRoles = schoolRoleData.schoolRoles.filter((existingSchoolRole) => existingSchoolRole.id() != schoolRole.id())

    if (schoolRoles.length > 0) {
      this.setState({
        schoolRolesPerSchool: {
          ...this.s.schoolRolesPerSchool,
          [schoolRole.parentId()]: {
            ...schoolRolesForSchool,
            [schoolRole.childId()]: {
              ...schoolRoleData,
              schoolRoles
            }
          }
        }
      })
    } else {
      const nextSchoolRolesForSchool = {...schoolRolesForSchool}

      delete nextSchoolRolesForSchool[schoolRole.childId()]

      this.setState({
        schoolRolesPerSchool: {
          ...this.s.schoolRolesPerSchool,
          [schoolRole.parentId()]: nextSchoolRolesForSchool
        }
      })
    }
  }
}

export default memo(shapeComponent(RoutesUserMyUsersRoute))
